import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

/** The header that turns a write into a dry run. From `Validation.HEADER_NAME` in the service. */
export const NF_VALIDATE_HEADER = 'nf-validate';

/** How long a check may take, so a field never waits on one that does not settle. */
export const FRONTDOOR_VALIDATE_TIMEOUT_MS = 15000;

/** The methods the dry-run endpoints are mapped on. A create is a `POST`, an update either of the others. */
export type FrontdoorCheckMethod = 'POST' | 'PUT' | 'PATCH';

/**
 * Asks whether a body would be accepted, and writes nothing. Takes the url and body already built
 * for the real call, because the `nf-validate` header is the whole of the routing.
 *
 * The answer is 200 with the violations, each carrying the JSON path of its field, so a refusal
 * is never a status. The method picks an update's rules: `PUT` demands every mutable field,
 * `PATCH` takes what it is given.
 */
export function checkFrontdoorBody(
    http: HttpClient,
    url: string,
    body: any,
    method: FrontdoorCheckMethod = 'POST'
): Observable<ValidationError[]> {
    /* Plain JSON: the answer is a bare array, and the HAL converter only writes `RepresentationModel`. */
    const headers = new HttpHeaders().set('Accept', 'application/json').set(NF_VALIDATE_HEADER, 'true');
    return sendWith(http, url, body, method, headers).pipe(
        timeout(FRONTDOOR_VALIDATE_TIMEOUT_MS),
        map((errors) => errors ?? [])
    );
}

function sendWith(
    http: HttpClient,
    url: string,
    body: any,
    method: FrontdoorCheckMethod,
    headers: HttpHeaders
): Observable<ValidationError[]> {
    if (method === 'POST') {
        return http.post<ValidationError[]>(url, body, { headers });
    }
    if (method === 'PUT') {
        return http.put<ValidationError[]>(url, body, { headers });
    }
    return http.patch<ValidationError[]>(url, body, { headers });
}
